import React, { useState, Fragment, useEffect, useRef } from "react";
import logo from "../../assets/logo-2x.png";
import { connect } from "react-redux";
import { signIn, signInWithRedirect, getCurrentUser } from "aws-amplify/auth";
import VerifyAccount from "./partials/VerifyAccount";
import ForgotPassword from "./partials/ForgotPassword";
import CreateAccount from "./partials/CreateAccount";
import NoOrganizationRoles from "./partials/NoOrganizationRoles";
import NewPassword from "./partials/NewPassword";
import EmailOTP from "./partials/EmailOTP";
import { updateLoginStatus } from "../../js/actions";
import apiHandler from "../../js/apiHandler";
import LoadingSpinner from "../global/LoadingSpinner";
import {
	isValidEmail,
	normalizeEmail,
	formatMessage,
} from "../../js/formUtils";
import appleLogin from "../../assets/icons/apple-login.svg";
import googleLogin from "../../assets/icons/google-login.svg";

/**
 * Login Component - Implements AWS Amplify login flow
 *
 * Props:
 * @param {function} onLogin - Callback function called after successful login
 * @param {function} getPermissions - Callback function to fetch user permissions
 * @param {object} history - React Router history object for navigation
 * @param {string} initialView - Initial view to display (default: "login")
 * @param {string} email - Initial email value
 * @param {boolean} isEmailVerified - Initial email verification status
 * @param {boolean} isPhoneVerified - Initial phone verification status
 * @param {function} clearTokenId - Callback function to clear token ID
 */

function Login(props) {
	const [firstName, setFirstName] = useState("");
	const [lastName, setLastName] = useState("");
	const [email, setEmail] = useState("");
	const [phoneNumber, setPhoneNumber] = useState("");
	const [password, setPassword] = useState("");
	const [error, setError] = useState("");
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [signInResponse, setSignInResponse] = useState(null);
	const [showForgotPassword, setShowForgotPassword] = useState(false);
	const [showCreateAccount, setShowCreateAccount] = useState(false);
	const [showVerifyAccount, setShowVerifyAccount] = useState(false);
	const [verifySignInStep, setVerifySignInStep] = useState(null);
	const [showNoOrganizationRoles, setShowNoOrganizationRoles] = useState(false);
	const [showNewPassword, setShowNewPassword] = useState(false);
	const [showEmailOTP, setShowEmailOTP] = useState(false);
	const [isPhoneVerified, setIsPhoneVerified] = useState(false);
	const [isEmailVerified, setIsEmailVerified] = useState(false);
	const [organizationCreationStatusUrl, setOrganizationCreationStatusUrl] =
		useState("");
	const [isWideLayout, setIsWideLayout] = useState(false);
	const hideSocialLogin = new URLSearchParams(window.location.search).has(
		"hs_id",
	);

	// Handle initialView prop changes
	useEffect(() => {
		if (props.initialView === "createAccount") {
			props.clearTokenId();
			setShowCreateAccount(true);
		} else if (props.initialView === "verifyAccount") {
			setShowVerifyAccount(true);
		} else if (props.initialView === "noOrganizationRoles") {
			props.clearTokenId();
			setShowVerifyAccount(false);
			setShowForgotPassword(false);
			setShowNoOrganizationRoles(true);
		}
		if (props.email !== "") {
			setEmail(props.email);
		}
		if (props.isEmailVerified !== "") {
			setIsEmailVerified(props.isEmailVerified);
		}
		if (props.isPhoneVerified !== "") {
			setIsPhoneVerified(props.isPhoneVerified);
		}
	}, [
		props.initialView,
		props.email,
		props.isEmailVerified,
		props.isPhoneVerified,
	]);

	// Check for OAuth errors that happened during app initialization
	useEffect(() => {
		const oauthError = sessionStorage.getItem("oauth_error");
		if (oauthError) {
			setError(
				"We had a problem logging into your account. Please try again, or login with another method.",
			);
			// Clear the error so it doesn't show again
			sessionStorage.removeItem("oauth_error");
		}
	}, []);

	// signInWithRedirect navigates the whole page away and never resolves its promise,
	// so isSubmitting is left "true" in this render's state forever. If the user hits
	// the browser back button before completing the OAuth flow, the browser restores
	// this page from the bfcache with that stale state instead of remounting, leaving
	// the form permanently disabled. Reset on bfcache restoration.
	useEffect(() => {
		const handlePageShow = (e) => {
			if (e.persisted) {
				setIsSubmitting(false);
			}
		};
		window.addEventListener("pageshow", handlePageShow);
		return () => window.removeEventListener("pageshow", handlePageShow);
	}, []);

	// Handle email normalization with the shared utility
	const handleEmailInput = (value) => {
		const normalized = normalizeEmail(value);
		setEmail(normalized);
		return normalized;
	};

	// Form validation using shared utilities
	const isEmailValid = () => isValidEmail(email);
	const isFormValid = () => isEmailValid() && password.length > 0;

	const wait = (delay) =>
		new Promise((resolve) => {
			setTimeout(resolve, delay);
		});

	const pollOrganizationCreationStatus = async (statusUrl) => {
		const startTime = Date.now();
		let forbiddenCount = 0;
		while (true) {
			if (Date.now() - startTime > 120000) {
				throw new Error(
					"Organization setup is taking longer than expected. Please try again.",
				);
			}
			let statusResponse;
			try {
				statusResponse = await apiHandler.get(statusUrl);
			} catch (pollError) {
				if (
					pollError.status === 403 ||
					pollError?.response?.status === 403 ||
					pollError?.statusCode === 403 ||
					// Sometimes errors are wrapped or have different structures
					pollError.message === "Network Error" ||
					pollError.message?.includes("network error")
				) {
					// Also count "Network Error" as potentially transient 403-masking or connectivity blip
					forbiddenCount++;
					// If it really was a 403 or network error, let's keep retrying a few times
					if (forbiddenCount <= 3) {
						await wait(3000);
						continue;
					}
				}
				throw pollError;
			}
			const statusValue = statusResponse?.data?.status;
			let responseValue = statusResponse?.data?.response;

			// Try to parse the response field if it's a string
			if (typeof responseValue === "string") {
				try {
					responseValue = JSON.parse(responseValue);
				} catch (e) {
					// Leave it as a string if it fails to parse
				}
			}

			if (
				statusValue === "in_progress_after_user_roles_assigned" ||
				statusValue === "completed" ||
				(responseValue && responseValue.user_id)
			) {
				return statusResponse?.data;
			}
			if (
				statusValue === "created" ||
				statusValue === "processing" ||
				(typeof statusValue === "string" &&
					statusValue.startsWith("in_progress"))
			) {
				await wait(3000);
				continue;
			}
			if (statusValue === "error") {
				const responseMessage =
					typeof responseValue === "string"
						? formatMessage(responseValue)
						: responseValue
							? JSON.stringify(responseValue)
							: "Organization creation failed. Please try again.";
				throw new Error(responseMessage);
			}
			return statusResponse?.data;
		}
	};

	const isUserSignedIn = async () => {
		try {
			const currentUser = await getCurrentUser();
			return !!currentUser;
		} catch {
			return false;
		}
	};

	const handlePostLoginSteps = (nextStep, user) => {
		switch (nextStep) {
			case "CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED":
				setSignInResponse(user);
				setShowNewPassword(true);
				break;

			case "CONFIRM_SIGN_IN_WITH_EMAIL_CODE":
				setSignInResponse(user);
				setShowEmailOTP(true);
				break;

			case "CONFIRM_SIGN_IN_WITH_SMS_CODE":
				setVerifySignInStep(nextStep);
				setShowVerifyAccount(true);
				break;

			case "RESET_PASSWORD":
				setShowForgotPassword(true);
				break;

			case "CONFIRM_SIGN_UP":
				setVerifySignInStep(nextStep);
				setShowVerifyAccount(true);
				break;

			default:
				setError(`Authentication step '${nextStep}' is not yet supported.`);
				break;
		}
	};

	const loginUser = async (newEmail = null, newPassword = null) => {
		// Sign out user if we're not auto signing in.
		if (!newEmail && !newPassword) {
			await props.onLogout(false);
		}

		let user;
		let isAlreadySignedIn = false;

		setIsSubmitting(true);
		setError("");

		if (newEmail) {
			setEmail(newEmail);
		}
		if (newPassword) {
			setPassword(newPassword);
		}

		await props.onLogin(false);

		try {
			// Check if user is already signed in
			isAlreadySignedIn = await isUserSignedIn();
			if (isAlreadySignedIn) {
				user = { isSignedIn: true };
			} else {
				user = await signIn({
					username: (newEmail || email).toLowerCase(),
					password: newPassword || password,
				});
			}

			// Amplify v6 returns { isSignedIn, nextStep: { signInStep } }
			const signInStep = user.nextStep?.signInStep || user.nextStep;

			// Skip post-login steps if already signed in
			if (
				!isAlreadySignedIn &&
				(signInStep === "NEW_PASSWORD_REQUIRED" ||
					signInStep === "CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED" ||
					signInStep === "CONFIRM_SIGN_IN_WITH_EMAIL_CODE" ||
					signInStep === "CONFIRM_SIGN_IN_WITH_SMS_CODE" ||
					signInStep === "RESET_PASSWORD" ||
					signInStep === "CONFIRM_SIGN_UP")
			) {
				// Hand off to the challenge screen (OTP/new password/etc) — it owns its
				// own submitting state from here. Without resetting this, the main
				// login button stays stuck on "Logging In..." if the user later backs
				// out of the challenge screen back to this form.
				setIsSubmitting(false);
				handlePostLoginSteps(signInStep, user);
				return;
			}

			// successful user sign In
			if (user) {
				if (organizationCreationStatusUrl) {
					try {
						await pollOrganizationCreationStatus(organizationCreationStatusUrl);
						setOrganizationCreationStatusUrl("");

						// Force refresh of current user cache after organization creation
						await apiHandler.get({
							apiName: "accapi",
							path: "/currentuser",
							options: {
								headers: {
									"Cache-Control": "must-revalidate",
								},
							},
						});
					} catch (pollError) {
						setIsSubmitting(false);
						setError(
							pollError.message ||
								"An error occurred during organization setup.",
						);
						return false;
					}
				}

				const permissionsResponse = await props.getPermissions();

				if (permissionsResponse && permissionsResponse.contact_info) {
					if (permissionsResponse.contact_info.first_name) {
						setFirstName(permissionsResponse.contact_info.first_name);
					}
					if (permissionsResponse.contact_info.last_name) {
						setLastName(permissionsResponse.contact_info.last_name);
					}
					if (permissionsResponse.contact_info.email) {
						setEmail(permissionsResponse.contact_info.email);
					}
					if (permissionsResponse.contact_info.mobile_phone) {
						setPhoneNumber(permissionsResponse.contact_info.mobile_phone);
					}
				}

				let currentEmailVerified = isEmailVerified;
				let currentPhoneVerified = isPhoneVerified;

				if (permissionsResponse && permissionsResponse.verification_status) {
					currentEmailVerified =
						permissionsResponse.verification_status.email_verified === true;
					currentPhoneVerified =
						permissionsResponse.verification_status.phone_verified === true;
					setIsEmailVerified(currentEmailVerified);
					setIsPhoneVerified(currentPhoneVerified);
				} else if (user.isSignedIn || signInStep === "DONE") {
					// User is CONFIRMED in Cognito but no verification_status from API
					// Assume verified since Amplify confirmed the sign-in
					currentEmailVerified = true;
					currentPhoneVerified = true;
					setIsEmailVerified(true);
					setIsPhoneVerified(true);
				}

				if (!currentEmailVerified) {
					setShowNoOrganizationRoles(false);
					setShowVerifyAccount(true);
				} else {
					props.onLogin(true);
				}
				setIsSubmitting(false);
			}
			return true;
		} catch (err) {
			if (err.code === "UserNotConfirmedException") {
				setIsSubmitting(false);
				setShowVerifyAccount(true);
			} else {
				if (err.code === "UserNotFoundException") {
					setError("Please check your email and password and try again.");
				} else {
					await handleLogout();
					let errorMessage =
						"Please check your email and password and try again.";
					if (err.message) {
						errorMessage = err.message;
					} else if (
						err.response &&
						err.response.data &&
						err.response.data.error
					) {
						errorMessage = formatMessage(err.response.data.error);
					}
					setError(errorMessage);
				}
				setIsSubmitting(false);
			}
			return false;
		}
	};

	const handleSubmit = async (e) => {
		e.preventDefault();

		if (!isFormValid()) {
			setError("Please enter a valid email and password.");
			return;
		}

		loginUser();
	};

	/**
	 * Sign in with AWS Amplify OAuth provider using OAuth redirect
	 * @returns {Promise} Promise that resolves when redirect is initiated
	 */
	const signInWithProvider = async (provider) => {
		try {
			setIsSubmitting(true);
			setError("");
			await props.onLogout(false);
			await signInWithRedirect({ provider: provider });
		} catch (error) {
			// Handle specific OAuth errors
			let errorMessage =
				error.message ||
				`Failed to sign in with ${provider}. Please try again.`;
			if (error.message && error.message.includes("invalid_grant")) {
				errorMessage = `${provider} authentication failed. This may be due to an expired or invalid authorization code. Please try signing in again.`;
			}

			setError(errorMessage);
			setIsSubmitting(false);
		}
	};

	const handleLogout = async (e) => {
		if (e) {
			e.preventDefault();
		}

		// Clear any pending specialized login state to prevent pollution of future login attempts
		setOrganizationCreationStatusUrl("");
		setSignInResponse(null);

		try {
			props.clearTokenId();
			await props.onLogout(false);
			props.history.push("/");
		} catch (err) {
			console.error(err);
		}
	};

	const handleAccountCreated = async (
		firstName,
		lastName,
		email,
		phoneNumber,
		password,
		organizationRoles,
		nextStep,
		organizationStatusUrl,
	) => {
		setFirstName(firstName);
		setLastName(lastName);
		setEmail(email);
		setPhoneNumber(phoneNumber);
		setPassword(password);
		setOrganizationCreationStatusUrl(organizationStatusUrl || "");
		setShowCreateAccount(false);
		setShowVerifyAccount(true);
	};

	const handleVerifyAccountSuccess = async () => {
		const isLogginSuccessful = await loginUser(email, password);
		if (!isLogginSuccessful) {
			setShowVerifyAccount(false);
		}
	};

	return (
		<div className="main-container">
			<div className="page-container form-wrap">
				<div
					className="login-container"
					style={{
						maxWidth: isWideLayout ? "900px" : undefined,
						transition: "max-width 0.3s ease",
					}}>
					<div>
						<img src={logo} alt="RedAwning" className="redawning-login-logo" />
					</div>
					{!showForgotPassword &&
						!showVerifyAccount &&
						!showNoOrganizationRoles &&
						!showNewPassword &&
						!showEmailOTP && (
							<Fragment>
								{!showCreateAccount ? (
									<Fragment>
										<h3>Welcome! Log into your account</h3>
										<p>
											Enter your credentials to access your RedAwning account.
										</p>

										{error && <div className="error-message">{error}</div>}

										<form
											onSubmit={handleSubmit}
											className="login-form"
											autoComplete="on">
											<div className="form-group">
												<input
													type="email"
													id="login-email"
													value={email}
													placeholder=" "
													onInput={(e) =>
														handleEmailInput(e.currentTarget.value)
													}
													onChange={(e) =>
														handleEmailInput(e.currentTarget.value)
													}
													onFocus={(e) => {
														// Check for autofill on first interaction
														if (e.target.value && e.target.value !== email) {
															setEmail(e.target.value);
														}
													}}
													required
													disabled={isSubmitting}
													autoComplete="email"
												/>
												<label htmlFor="login-email">Email Address</label>
												{email && !isEmailValid() && (
													<div className="field-error">
														Please enter a valid email address
													</div>
												)}
											</div>

											<div className="form-group">
												<input
													type="password"
													id="login-password"
													value={password}
													placeholder=" "
													onInput={(e) => setPassword(e.currentTarget.value)}
													onChange={(e) => setPassword(e.currentTarget.value)}
													onFocus={(e) => {
														// Check for autofill on first interaction
														if (e.target.value && e.target.value !== password) {
															setPassword(e.target.value);
														}
													}}
													required
													disabled={isSubmitting}
													autoComplete="current-password"
												/>
												<label htmlFor="login-password">Password</label>
											</div>

											<div className="form-actions">
												<button
													type="submit"
													className="submit-button"
													disabled={!isFormValid() || isSubmitting}>
													{isSubmitting ? "Logging In..." : "Log In"}
												</button>
											</div>

											<div className="form-links">
												<a
													href="#"
													className="form-text-link"
													onClick={(e) => {
														e.preventDefault();
														setShowForgotPassword(true);
													}}>
													Forgot your password?
												</a>
											</div>

											<div className="form-links">
												New to RedAwning?{" "}
												<a
													href="#"
													className="form-text-link"
													onClick={(e) => {
														e.preventDefault();
														props.clearTokenId();
														setShowCreateAccount(true);
													}}>
													Create your account
												</a>
											</div>
										</form>
									</Fragment>
								) : (
									<CreateAccount
										firstName={firstName}
										lastName={lastName}
										email={email}
										onAccountCreated={handleAccountCreated}
										onCancel={() => {
											setShowCreateAccount(false);
											setIsWideLayout(false);
										}}
										isInitialView={props.initialView === "createAccount"}
										onLayoutChange={setIsWideLayout}
									/>
								)}

								{!hideSocialLogin && (
									<>
										<div className="social-divider">
											<span>or</span>
										</div>

										<div className="social-login-buttons">
											<button
												type="button"
												className="social-button google-button"
												onClick={() => signInWithProvider("Google")}
												disabled={isSubmitting}>
												<img src={googleLogin} alt="Google" />
												Continue with Google
											</button>

											<button
												type="button"
												className="social-button apple-button"
												onClick={() => signInWithProvider("Apple")}
												disabled={isSubmitting}>
												<img src={appleLogin} alt="Apple" />
												Continue with Apple
											</button>
										</div>
									</>
								)}
							</Fragment>
						)}

					{showNoOrganizationRoles && (
						<NoOrganizationRoles
							firstName={firstName || props.firstName || ""}
							email={email || props.email || ""}
							onCancel={() => {
								setShowNoOrganizationRoles(false);
								handleLogout();
							}}
							onLogout={() => {
								setShowNoOrganizationRoles(false);
								handleLogout();
							}}
						/>
					)}

					{showForgotPassword && (
						<ForgotPassword
							email={email}
							signInResponse={signInResponse}
							onPasswordResetSuccess={(email, newPassword) =>
								loginUser(email, newPassword)
							}
							onCancel={() => {
								setShowForgotPassword(false);
								handleLogout();
							}}
						/>
					)}

					{showVerifyAccount && (
						<VerifyAccount
							firstName={firstName}
							email={email}
							signInStep={verifySignInStep}
							phoneNumber={phoneNumber}
							isPhoneVerified={isPhoneVerified}
							isEmailVerified={isEmailVerified}
							organizationCreationStatusUrl={organizationCreationStatusUrl}
							onVerificationSuccess={handleVerifyAccountSuccess}
							onCancel={() => {
								setShowVerifyAccount(false);
								handleLogout();
							}}
						/>
					)}

					{showNewPassword && (
						<NewPassword
							email={email}
							user={signInResponse}
							onNewPasswordSuccess={(email, newPassword) => {
								setShowNewPassword(false);
								loginUser(email, newPassword);
							}}
							onCancel={() => {
								setShowNewPassword(false);
								handleLogout();
							}}
						/>
					)}

					{showEmailOTP && (
						<EmailOTP
							email={email}
							user={signInResponse}
							onEmailCodeSuccess={async () => {
								setIsSubmitting(true);
								setError("");
								try {
									await props.getPermissions();
									setShowEmailOTP(false);
									props.onLogin(true);
								} catch (err) {
									setError(
										err?.message ||
											"We couldn't finish signing you in. Please try again.",
									);
								} finally {
									setIsSubmitting(false);
								}
							}}
							onCancel={() => {
								setShowEmailOTP(false);
								handleLogout();
							}}
						/>
					)}
				</div>
			</div>
		</div>
	);
}

const mapStateToProps = (state) => {
	return {
		user: state.authState.user,
		roleManager: state.roleManager,
	};
};
const mapDispatchToProps = (dispatch) => {
	return {
		updateLoginStatus: (isLoggedIn) => {
			dispatch(updateLoginStatus(isLoggedIn));
		},
	};
};

export default connect(mapStateToProps, mapDispatchToProps)(Login);
